import React, { useMemo, useState } from 'react';
import {
  AuthCertificateData,
  AuthCertificateWithLoginSchema,
  CERTIFICATES,
  certificates,
  CLOUD_CERTIFICATES,
} from './certificates';
import { CertificateService } from './certificateService';
import { useLoading } from '../../helpers/useLoading';
import { toast } from '../../helpers/toast';
import { Button } from '../../components/UI-CHAKRA/Button';
import { Text } from '../../components/UI-CHAKRA/Text';
import { Spinner } from '../../components/UI-CHAKRA/Spinner';
import { FormLabel } from '../../components/UI-CHAKRA/FormLabel';
import { Input } from '../../components/UI-CHAKRA/Input/Input';
import { FormControl } from '../../components/UI-CHAKRA/FormControl';
import BaseDialog from '../../components/Dialogs/BaseDialog';
import { Flex } from '../../components/UI-CHAKRA/Flex';
import { Form, Formik } from 'formik';
import { FormErrorMessage } from '../../components/UI-CHAKRA/FormErrorMessage';
import { usePrescriptionStore } from '../../stores/prescriptionStore';

interface AuthenticateCloudCertificateWithLoginProps {
  onCancel(): void;
  onAuthenticated(): void;
  open: boolean;
  prescriptionId: number;
  provider: CLOUD_CERTIFICATES;
  needsOTP: boolean;
}

export function AuthenticateCloudCertificateWithLogin(props: AuthenticateCloudCertificateWithLoginProps) {
  const [open, setOpen] = useState(props.open);
  const [loading, runWithLoading] = useLoading();
  const [hasApiErrors, setHasApiErrors] = useState(false);

  const prescriber = usePrescriptionStore((store) => store.prescriber());

  const onConfirm = async ({ email, otp, pin }: AuthCertificateData) => {
    if (loading) return;

    runWithLoading(async () => {
      try {
        switch (props.provider) {
          case CERTIFICATES.REMOTE_ID: {
            const payload = props.needsOTP
              ? {
                  email,
                  pin,
                  token: otp,
                }
              : { pin };
            await CertificateService.remoteId.authenticate(props.prescriptionId, payload);
            break;
          }
        }

        setHasApiErrors(false);
        setOpen(false);
        props.onAuthenticated();
      } catch (e) {
        setHasApiErrors(true);
        toast.error(props.needsOTP ? 'PIN e/ou e-Token está(ão) inválido' : 'PIN inválido');
      }
    });
  };

  const onCancel = () => {
    props.onCancel();
    setOpen(false);
  };

  const certificateItem = useMemo(() => {
    return certificates.find((certificate) => certificate.value === props.provider);
  }, [props.provider]);

  const certificateProps = useMemo(() => {
    switch (props.provider) {
      case CERTIFICATES.REMOTE_ID: {
        return {
          pinType: 'password',
          otpType: 'text',
          inputLength: 255,
          pinInputLabel: 'PIN',
          tokenInputLabel: 'e-Token',
          dialogTitle: !props.needsOTP
            ? 'Faça login no seu certificado'
            : 'Faça login no seu certificado e insira o e-token',
          dialogSubtitle: '',
          loginIdentificatoinText: 'E-mail',
          loginIdentificationType: 'email',
        };
      }
    }
  }, [props.provider, props.needsOTP]);

  return (
    <BaseDialog
      isOpen={open}
      onClose={() => {
        props.onCancel();
      }}
    >
      <Formik
        initialValues={{ email: prescriber?.Email || '', otp: '', pin: '' }}
        validationSchema={AuthCertificateWithLoginSchema}
        onSubmit={onConfirm}
        validateOnBlur={true}
        validateOnChange={false}
      >
        {(formik) => {
          return (
            <Form style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Flex flexDirection="column" alignItems="center" gap="20px">
                <Flex maxWidth="120px">
                  <img src={certificateItem?.imagePath} alt={certificateItem?.name} />
                </Flex>
                <Flex>
                  <Text fontSize="18px" fontWeight={700}>
                    {certificateProps?.dialogTitle}
                  </Text>
                </Flex>
              </Flex>

              <FormControl isInvalid={hasApiErrors || !!formik.errors.email} mt="20px">
                <FormLabel>{certificateProps?.loginIdentificatoinText}</FormLabel>
                <Input
                  w={'100%'}
                  name="email"
                  maxLength={certificateProps?.inputLength}
                  type={certificateProps?.loginIdentificationType}
                  autoFocus={!prescriber?.Email}
                  data-testid={'auth-cloud-input-email'}
                  onChange={formik.handleChange}
                  isDisabled={loading}
                  defaultValue={prescriber?.Email}
                />
                <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
              </FormControl>

              <Flex gap="20px">
                <FormControl isInvalid={hasApiErrors || !!formik.errors.pin} mt="10px">
                  <FormLabel>{certificateProps?.pinInputLabel}</FormLabel>
                  <Input
                    w={'100%'}
                    name="pin"
                    maxLength={certificateProps?.inputLength}
                    type={certificateProps?.pinType}
                    data-testid={'auth-cloud-input-pin'}
                    onChange={formik.handleChange}
                    isDisabled={loading}
                    autoFocus={!!prescriber?.Email}
                  />
                  <FormErrorMessage>{formik.errors.pin}</FormErrorMessage>
                </FormControl>

                {props.needsOTP && (
                  <FormControl isInvalid={hasApiErrors || !!formik.errors.otp} marginTop={'10px'}>
                    <FormLabel>{certificateProps?.tokenInputLabel}</FormLabel>
                    <Input
                      w={'100%'}
                      maxLength={certificateProps?.inputLength}
                      type={certificateProps?.otpType}
                      data-testid={'auth-cloud-input-token'}
                      name="otp"
                      onChange={formik.handleChange}
                      isDisabled={loading}
                    />
                    <FormErrorMessage>{formik.errors.otp}</FormErrorMessage>
                  </FormControl>
                )}
              </Flex>
              <Flex marginTop={'20px'} gap={'10px'} width="100%">
                <Button
                  width={'100%'}
                  onClick={onCancel}
                  variant={'outline'}
                  bg={'white'}
                  color={'error.main'}
                  borderColor="error.main"
                  fontWeight={700}
                  fontSize={'14px'}
                  borderRadius="40px"
                  padding="20px"
                  height="50px"
                  isDisabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  w={'100%'}
                  type="submit"
                  variant={'solid'}
                  backgroundColor={'primary.main'}
                  fontWeight={700}
                  borderRadius="40px"
                  height="50px"
                >
                  {loading ? <Spinner color={'gray.light'} minH={'18px'} minW={'18px'} /> : 'Confirmar'}
                </Button>
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </BaseDialog>
  );
}
